<template>
  <div v-if="isLoggedIn" class="ai-sidebar" :class="{ open: isOpen }">
    <!-- 展开按钮 -->
    <div class="sidebar-toggle" @click="toggleSidebar">
      <i :class="isOpen ? 'el-icon-close' : 'el-icon-chat-line-round'"></i>
      <span v-if="!isOpen">AI</span>
    </div>

    <!-- 侧边栏主体 -->
    <div class="sidebar-panel" :style="{ width: sidebarWidth + 'px' }">
      <!-- 左侧拖拽调整大小的手柄 -->
      <div class="resize-handle" @mousedown="onResizeStart"></div>
      <!-- 头部 -->
      <div class="sidebar-header">
        <div class="header-left">
          <div class="ai-avatar">
            <i class="el-icon-magic-stick"></i>
          </div>
          <div class="header-title">
            <span class="title-main">青苹果知识库 AI</span>
            <span class="title-sub">智能问答助手</span>
          </div>
        </div>
        <div class="header-actions">
          <el-tooltip content="新建对话" placement="bottom">
            <button class="icon-btn" @click="createConversation">
              <i class="el-icon-plus"></i>
            </button>
          </el-tooltip>
          <el-tooltip content="清空当前对话" placement="bottom">
            <button class="icon-btn" @click="clearCurrentConversation" :disabled="!currentMessages.length">
              <i class="el-icon-delete"></i>
            </button>
          </el-tooltip>
          <button class="icon-btn" @click="toggleSidebar">
            <i class="el-icon-d-arrow-right"></i>
          </button>
        </div>
      </div>

      <!-- 对话目录条（问题跳转） -->
      <div v-if="questionList.length" class="question-strip">
        <div class="strip-label" @click="stripCollapsed = !stripCollapsed">
          <i :class="stripCollapsed ? 'el-icon-caret-right' : 'el-icon-caret-bottom'"></i>
          <span>目录</span>
          <span class="strip-count">{{ questionList.length }}</span>
          <i :class="['el-icon-arrow-up', 'strip-toggle', { collapsed: stripCollapsed }]"></i>
        </div>
        <div class="strip-pills" v-show="!stripCollapsed">
          <div
            v-for="(q, i) in questionList"
            :key="q.idx"
            class="pill-item"
            @click="jumpToMessage(q.idx)"
          >
            <span class="pill-num">{{ i + 1 }}</span>
            <span class="pill-text">{{ q.preview }}</span>
            <div class="pill-popup">
              <div class="popup-content">{{ q.full }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 对话列表 -->
      <div class="conversation-list" :class="{ collapsed: convListCollapsed }">
        <div class="list-header" @click="convListCollapsed = !convListCollapsed">
          <span>
            <i :class="convListCollapsed ? 'el-icon-caret-right' : 'el-icon-caret-bottom'"></i>
            对话记录
          </span>
          <span class="conv-count">{{ conversations.length }}</span>
        </div>
        <div class="conv-items" v-show="!convListCollapsed">
          <div
            v-for="conv in conversations"
            :key="conv.id"
            :class="['conv-item', { active: conv.id === activeConvId }]"
            @click="switchConversation(conv.id)"
          >
            <div class="conv-icon">
              <i class="el-icon-chat-dot-round"></i>
            </div>
            <div class="conv-info">
              <div class="conv-name">{{ conv.name }}</div>
              <div class="conv-time">{{ formatDate(conv.updatedAt) }}</div>
            </div>
            <div class="conv-actions" @click.stop>
              <el-tooltip content="删除" placement="top">
                <button class="del-btn" @click="deleteConversation(conv.id)">
                  <i class="el-icon-close"></i>
                </button>
              </el-tooltip>
            </div>
          </div>
          <div v-if="!conversations.length" class="empty-convs">
            <i class="el-icon-chat-line-round"></i>
            <p>暂无对话记录</p>
            <p class="hint">点击上方 + 新建对话</p>
          </div>
        </div>
      </div>

      <!-- 消息区域 -->
      <div class="chat-area">
        <div class="chat-messages" ref="messagesRef">
          <!-- 欢迎页 -->
          <div v-if="!currentMessages.length" class="welcome-section">
            <div class="welcome-icon">
              <i class="el-icon-magic-stick"></i>
            </div>
            <h2>你好，有什么可以帮助你的？</h2>
            <p>你可以问我关于历史资料、新闻报道、图片等方面的问题</p>
            <div class="quick-prompts">
              <button
                v-for="(prompt, idx) in quickPrompts"
                :key="idx"
                class="prompt-chip"
                @click="usePrompt(prompt.text)"
              >
                <i :class="prompt.icon"></i>
                {{ prompt.text }}
              </button>
            </div>
          </div>

          <!-- 消息列表 -->
          <template v-else>
            <div
              v-for="(msg, index) in currentMessages"
              :key="index"
              :id="`msg_${index}`"
              :class="['message-item', msg.type, { 'msg-active': activeMsgId === index }]"
            >
              <div class="message-avatar">
                <i :class="msg.type === 'user' ? 'el-icon-user' : 'el-icon-magic-stick'"></i>
              </div>
              <div class="message-content">
                <div class="message-bubble">
                  <div v-if="msg.type === 'user'" class="message-text">
                    {{ msg.content }}
                  </div>
                  <template v-else>
                    <div class="message-text answer-text">
                      <div v-html="formatMessage(msg.content)"></div>
                    </div>
                    <div v-if="msg.results && msg.results.length" class="result-cards">
                      <div
                        v-for="(item, cardIdx) in msg.results"
                        :key="cardIdx"
                        class="result-card"
                        :class="item.sourceType"
                        @click="openDetail(item)"
                      >
                        <template v-if="item.sourceType === 'image'">
                          <div class="card-thumb">
                            <img
                              v-if="item.imageUrl"
                              :src="item.imageUrl"
                              :alt="item.title || '图片'"
                              @error="onImgError"
                            />
                            <div v-else class="card-thumb-placeholder">
                              <i class="el-icon-picture-outline"></i>
                            </div>
                          </div>
                          <div class="card-info">
                            <div class="card-type-tag">
                              <i class="el-icon-picture-outline"></i>
                              <span>图片</span>
                            </div>
                            <div class="card-title">{{ item.title || item.tags || '历史图片' }}</div>
                            <div class="card-meta">
                              <span v-if="item.source">{{ item.source }}</span>
                              <span v-if="item.date">{{ item.date }}</span>
                            </div>
                          </div>
                        </template>
                        <template v-else>
                          <div class="card-info">
                            <div class="card-type-tag">
                              <i class="el-icon-document"></i>
                              <span>文章</span>
                            </div>
                            <div class="card-title">{{ item.title || '未知标题' }}</div>
                            <div class="card-summary" v-if="item.content">
                              {{ truncateText(item.content, 80) }}
                            </div>
                            <div class="card-meta">
                              <span v-if="item.source">{{ item.source }}</span>
                              <span v-if="item.date">{{ item.date }}</span>
                              <span v-if="item.author">{{ item.author }}</span>
                            </div>
                          </div>
                          <div class="card-arrow">
                            <i class="el-icon-arrow-right"></i>
                          </div>
                        </template>
                      </div>
                    </div>
                  </template>
                </div>
                <div class="message-time">{{ msg.time }}</div>
              </div>
            </div>

            <!-- 加载中 -->
            <div v-if="loading" class="message-item assistant">
              <div class="message-avatar">
                <i class="el-icon-magic-stick"></i>
              </div>
              <div class="message-content">
                <div class="message-bubble">
                  <div class="message-text loading">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- 底部输入区 -->
        <div class="chat-footer">
          <div class="source-select">
            <el-radio-group v-model="sourceType" size="mini">
              <el-radio-button label="all">
                <i class="el-icon-menu"></i> 全部
              </el-radio-button>
            </el-radio-group>
          </div>
          <div class="input-wrapper">
            <el-input
              v-model="inputMessage"
              type="textarea"
              :rows="2"
              resize="none"
              placeholder="输入问题，Ctrl+Enter 发送..."
              @keydown.enter.ctrl="sendMessage"
              @keydown.enter.exact.prevent
              :disabled="loading"
              @input="autoResize"
              ref="inputRef"
            ></el-input>
            <button
              class="send-btn"
              :class="{ active: inputMessage.trim() }"
              :disabled="!inputMessage.trim() || loading"
              @click="sendMessage"
            >
              <i class="el-icon-position"></i>
              <span>发送</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ragChatWithResults } from '@/api/rag/rag'
import { encrypt } from '@/utils/crypt.js'

const STORAGE_KEY = 'ai_chat_conversations'
const MAX_CONVERSATIONS = 20

export default {
  name: 'AiChatFloat',
    data() {
      return {
        isOpen: false,
        inputMessage: '',
        sidebarWidth: 380,
        isResizing: false,
        startX: 0,
        startWidth: 0,
        activeMsgId: null,
        stripCollapsed: false,
        convListCollapsed: false,
      messages: [],
      conversations: [],
      activeConvId: null,
      loading: false,
      sourceType: 'all',
      quickPrompts: [
        { icon: 'el-icon-search', text: '查找辛亥革命相关报道' },
        { icon: 'el-icon-picture-outline', text: '展示历史图片' },
        { icon: 'el-icon-notebook-2', text: '民国时期的经济发展' },
        { icon: 'el-icon-data-analysis', text: '对比分析近代史' }
      ]
    }
  },
    computed: {
      isLoggedIn() {
        return !!(this.$ls && this.$ls.get('ACCESS_TOKEN'))
      },
      currentMessages: {
        get() {
          return this.messages
        },
        set(val) {
          this.messages = val
        }
      },
      questionList() {
        return this.messages
          .map((msg, idx) => ({ msg, idx }))
          .filter(({ msg }) => msg.type === 'user')
          .map(({ msg, idx }) => ({
            idx,
            preview: msg.content.trim().slice(0, 12),
            full: msg.content.trim()
          }))
      }
    },
  watch: {
    conversations: {
      deep: true,
      handler() {
        this.persistToStorage()
      }
    },
    messages: {
      deep: true,
      handler() {
        this.persistToStorage()
      }
    }
  },
  created() {
    this.loadFromStorage()
  },
  methods: {
    toggleSidebar() {
      this.isOpen = !this.isOpen
    },
    jumpToMessage(idx) {
      const el = document.getElementById(`msg_${idx}`)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      this.activeMsgId = idx
      setTimeout(() => {
        this.activeMsgId = null
      }, 1500)
    },
    onResizeStart(e) {
      this.isResizing = true
      this.startX = e.clientX
      this.startWidth = this.sidebarWidth
      document.addEventListener('mousemove', this.onResizing)
      document.addEventListener('mouseup', this.onResizeEnd)
      document.body.style.cursor = 'ew-resize'
      document.body.style.userSelect = 'none'
    },
    onResizing(e) {
      if (!this.isResizing) return
      const delta = this.startX - e.clientX
      const newWidth = Math.min(Math.max(this.startWidth + delta, 280), 700)
      this.sidebarWidth = newWidth
    },
    onResizeEnd() {
      this.isResizing = false
      document.removeEventListener('mousemove', this.onResizing)
      document.removeEventListener('mouseup', this.onResizeEnd)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    },
    persistToStorage() {
      if (!this.activeConvId) return
      const conv = this.conversations.find(c => c.id === this.activeConvId)
      if (conv) {
        conv.messages = this.messages
        conv.updatedAt = Date.now()
      }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.conversations))
      } catch (e) {
        console.warn('localStorage 存储失败', e)
      }
    },
    loadFromStorage() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const data = JSON.parse(stored)
          if (Array.isArray(data) && data.length > 0) {
            this.conversations = data
            const last = data[data.length - 1]
            this.activeConvId = last.id
            this.messages = last.messages || []
          }
        }
      } catch (e) {
        console.warn('localStorage 读取失败', e)
      }
    },
    createConversation() {
      if (this.conversations.length >= MAX_CONVERSATIONS) {
        this.$message.warning(`最多保留 ${MAX_CONVERSATIONS} 个对话记录，将自动清理最早的记录`)
        const oldest = this.conversations.reduce((a, b) =>
          a.updatedAt < b.updatedAt ? a : b
        )
        this.conversations = this.conversations.filter(c => c.id !== oldest.id)
      }

      const id = 'conv_' + Date.now()
      const newConv = {
        id,
        name: this.generateConvName(),
        messages: [],
        updatedAt: Date.now()
      }
      this.conversations.push(newConv)
      this.activeConvId = id
      this.messages = []
      this.persistToStorage()
      this.$nextTick(() => {
        if (this.$refs.messagesRef) {
          this.$refs.messagesRef.scrollTop = 0
        }
      })
    },
    generateConvName() {
      const count = this.conversations.length + 1
      return `对话 ${count}`
    },
    switchConversation(id) {
      this.activeConvId = id
      const conv = this.conversations.find(c => c.id === id)
      this.messages = conv ? (conv.messages || []) : []
      this.$nextTick(() => {
        if (this.$refs.messagesRef) {
          this.$refs.messagesRef.scrollTop = this.$refs.messagesRef.scrollHeight
        }
      })
    },
    deleteConversation(id) {
      this.$confirm('确定删除此对话记录？', '提示', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.conversations = this.conversations.filter(c => c.id !== id)
        if (this.activeConvId === id) {
          if (this.conversations.length > 0) {
            const last = this.conversations[this.conversations.length - 1]
            this.switchConversation(last.id)
          } else {
            this.activeConvId = null
            this.messages = []
          }
        }
        this.$message.success('对话已删除')
      }).catch(() => {})
    },
    clearCurrentConversation() {
      this.$confirm('确定清空当前对话？', '提示', {
        confirmButtonText: '清空',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.messages = []
        this.persistToStorage()
        this.$message.success('对话已清空')
      }).catch(() => {})
    },
    usePrompt(text) {
      if (!this.activeConvId) {
        this.createConversation()
      }
      this.inputMessage = text
      this.$nextTick(() => {
        if (this.$refs.inputRef && this.$refs.inputRef.focus) {
          this.$refs.inputRef.focus()
        }
      })
    },
    autoResize() {},
    async sendMessage() {
      const msg = this.inputMessage.trim()
      if (!msg || this.loading) return

      if (!this.activeConvId) {
        this.createConversation()
      }

      this.messages.push({
        type: 'user',
        content: msg,
        time: this.formatTime(new Date())
      })

      const tempInput = this.inputMessage
      this.inputMessage = ''
      this.loading = true

      this.$nextTick(() => {
        this.scrollToBottom()
      })

      try {
        const res = await ragChatWithResults({
          message: msg,
          sourceType: this.sourceType,
          limit: 5,
          similarityThreshold: 0.5,
          conversationId: this.activeConvId
        })

        const resultData = res && res.result
        const rawHits = resultData && resultData.hits
        const esHits = (rawHits && rawHits.data && rawHits.data.hits) ? rawHits.data.hits : []
        this.messages.push({
          type: 'assistant',
          content: (resultData && resultData.answer) || '抱歉，暂时无法回答您的问题。',
          results: this.transformHitsToCards(esHits),
          time: this.formatTime(new Date())
        })
      } catch (error) {
        console.error('RAG对话失败:', error)
        this.messages.push({
          type: 'assistant',
          content: '抱歉，发生了错误，请稍后重试。',
          results: [],
          time: this.formatTime(new Date())
        })
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messagesRef) {
          this.$refs.messagesRef.scrollTop = this.$refs.messagesRef.scrollHeight
        }
      })
    },
    transformHitsToCards(hits) {
      if (!hits || !Array.isArray(hits)) return []
      return hits.map(hit => {
        const src = (hit._source || hit.sourceAsMap) || {}
        // TODo zhou 这里因为目前仅返回文章 所以修改
        const isImage = (src.itemType === '200' || hit.type === 'image')
        return {
          id: src.id || hit.id || '',
          newsId: src.id || hit.id || '',
          sourceType: isImage ? 'image' : 'article',
          title: src.newsTitle || src.title || '未知标题',
          content: src.aiArticleSummary || src.pureContent || src.content || '',
          source: src.newsSource || src.paperName || '',
          date: src.paperDate ? src.paperDate.split(' ')[0] : '',
          author: src.newsAuthor || '',
          paperIssueNum: src.paperIssueNum || '',
          pageNo: src.pageNo || src.pageNum || '',
          pageSortNo: src.pageSortNo || '',
          pageName: src.pageName || '',
          paperDate: src.paperDate || '',
          paperName: src.paperName || '',
          imgId: src.id || '',
          imageUrl: src.imgThumPath ? (src.imgThumPath.startsWith('http') ? src.imgThumPath : `/${src.imgThumPath}`) : '',
          tags: src.aiLabel || src.newsPerson || ''
        }
      })
    },
    formatTime(date) {
      const pad = n => n < 10 ? '0' + n : n
      return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now - date
      if (diff < 60000) return '刚刚'
      if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`
      const pad = n => n < 10 ? '0' + n : n
      return `${date.getMonth() + 1}/${date.getDate()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    formatMessage(content) {
      if (content == null) return ''
      const str = String(content)

      // 1. 先处理代码块（包裹在 ``` 内的内容不进行后续转换）
      const codeBlocks = []
      let html = str.replace(/```(\w*)\n?([\s\S]*?)```/g, (match, lang, code) => {
        const safeCode = code
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
        const placeholder = `__CODEBLOCK_${codeBlocks.length}__`
        codeBlocks.push(`<pre class="md-code-block"><code>${safeCode}</code></pre>`)
        return placeholder
      })

      // 2. 处理行内代码
      html = html.replace(/`([^`]+)`/g, '<code class="md-inline-code">$1</code>')

      // 3. 转义普通文本中的 HTML 标签（防 XSS），但保留已转换的 Markdown 标签
      //    先保护已转换的标签
      const tagPlaceholders = []
      const protectTags = (text) => {
        return text.replace(/<(pre|code|b|strong|i|em|ul|ol|li|h[1-6]|br|blockquote)[^>]*>[\s\S]*?<\/\1>/g, (m) => {
          const ph = `__TAGPROTECT_${tagPlaceholders.length}__`
          tagPlaceholders.push(m)
          return ph
        })
      }
      const restoreTags = (text) => {
        return text.replace(/__TAGPROTECT_(\d+)__/g, (_, i) => tagPlaceholders[i])
      }

      html = protectTags(html)
      html = html.replace(/</g, '&lt;').replace(/>/g, '&gt;')
      html = restoreTags(html)

      // 4. Markdown → HTML 转换（按优先级从低到高，防止干扰）
      // 4.1 标题（从大到小，防止 ### 被 # 匹配）
      html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>')
      html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>')
      html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>')

      // 4.2 引用块
      html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>')

      // 4.3 粗体和斜体（粗体优先）
      html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
      html = html.replace(/_(.+?)_/g, '<em>$1</em>')

      // 4.4 删除线
      html = html.replace(/~~(.+?)~~/g, '<del>$1</del>')

      // 4.5 无序列表（支持 -, *, +）
      html = html.replace(/^[\*\-\+] (.+)$/gm, '<li>$1</li>')

      // 4.6 有序列表
      html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>')

      // 4.7 包裹连续的 <li> 为 <ul> 或 <ol>
      html = html.replace(/(<li>[\s\S]*?<\/li>\n?)+/g, (match) => {
        const trimmed = match.trim()
        if (/^\d+\./.test(trimmed)) {
          return `<ol>${trimmed}</ol>`
        }
        return `<ul>${trimmed}</ul>`
      })

      // 4.8 引用块多行合并
      html = html.replace(/(<blockquote>[\s\S]*?<\/blockquote>\n?)+/g, (match) => {
        const content = match.replace(/<\/?blockquote>/g, '').trim()
        return `<blockquote>${content}</blockquote>`
      })

      // 5. 换行符（独立行变成 <br>，但不在标签内）
      html = html.replace(/\n/g, '<br>')

      // 6. 恢复代码块
      html = html.replace(/__CODEBLOCK_(\d+)__/g, (_, i) => codeBlocks[i])

      return html
    },
    truncateText(text, maxLen) {
      if (!text) return ''
      const str = String(text).replace(/<[^>]*>/g, '').trim()
      return str.length > maxLen ? str.slice(0, maxLen) + '…' : str
    },
    onImgError(e) {
      e.target.style.display = 'none'
    },
    openDetail(item) {
      if (!item) return

      if (item.sourceType === 'image') {
        const imgInfor = {
          paperIssueNum: item.paperIssueNum || '',
          pageNum: item.pageNo || '',
          pageSortNo: item.pageSortNo || '1',
          newsId: item.newsId || '',
          imgId: item.id || '',
          imgSelfThumPath: item.imageUrl || '',
          paperDate: item.paperDate || '',
          paperName: item.source || '',
          pageName: item.pageName || ''
        }
        const routeUrl = this.$router.resolve({
          path: '/imageDetail',
          query: {
            imgInfor: JSON.stringify(imgInfor),
            searchParamStrList: JSON.stringify([])
          }
        })
        window.open(routeUrl.href, '_blank')
      } else {
        const routeUrl = this.$router.resolve({
          path: '/detail',
          query: {
            paperIssueNum: item.paperIssueNum ? encrypt(item.paperIssueNum) : '',
            pageNo: item.pageNo ? encrypt(item.pageNo) : '',
            pageSortNo: item.pageSortNo ? encrypt(item.pageSortNo) : '',
            newsId: item.id ? encrypt(item.id) : '',
            searchParamStrList: JSON.stringify([]),
            chineseType: ''
          }
        })
        window.open(routeUrl.href, '_blank')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$primary: linear-gradient(135deg, $mainColor 0%, #003a8c 100%);
$primary-solid: $mainColor;
$sidebar-width: 380px;

.ai-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  z-index: 2000;
  display: flex;
  pointer-events: none;

  &.open {
    .sidebar-panel {
      transform: translateX(0);
      box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
    }
  }

  .sidebar-toggle {
    position: fixed;
    right: 20px;
    bottom: 80px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: $mainColor;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(195, 0, 15, 0.45);
    z-index: 2001;
    pointer-events: auto;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    i {
      font-size: 22px;
      transition: transform 0.3s;
    }

    span {
      font-size: 13px;
      font-weight: bold;
      margin-left: 2px;
      letter-spacing: 1px;
    }

    &:hover {
      transform: scale(1.08);
      box-shadow: 0 6px 20px rgba(195, 0, 15, 0.55);

      i {
        transform: rotate(15deg);
      }
    }
  }

  .sidebar-panel {
    position: relative;
    width: $sidebar-width;
    height: 100%;
    background: #fafbfc;
    display: flex;
    flex-direction: column;
    pointer-events: auto;
    transform: translateX(100%);
    transition: width 0.05s ease, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    border-left: 1px solid rgba(0, 0, 0, 0.06);

    .resize-handle {
      position: absolute;
      left: 0;
      top: 0;
      width: 5px;
      height: 100%;
      cursor: ew-resize;
      z-index: 10;
      transition: background 0.2s;

      &::after {
        content: '';
        position: absolute;
        left: 1px;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 40px;
        border-radius: 2px;
        background: rgba(195, 0, 15, 0);
        transition: background 0.2s;
      }

      &:hover::after,
      &:active::after {
        background: rgba(195, 0, 15, 0.5);
      }
    }
  }
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: $mainColor;
  flex-shrink: 0;

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .ai-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: rgba(185, 0, 1, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      i {
        font-size: 20px;
        color: #fff;
      }
    }

    .header-title {
      display: flex;
      flex-direction: column;

      .title-main {
        font-size: 15px;
        font-weight: bold;
        color: #fff;
        letter-spacing: 0.5px;
      }

      .title-sub {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.75);
        margin-top: 1px;
      }
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 4px;

    .icon-btn {
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.2);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;
      padding: 0;

      i {
        font-size: 15px;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.35);
        transform: scale(1.05);
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        transform: none;
      }
    }
  }
}

.question-strip {
  flex-shrink: 0;
  border-bottom: 1px solid #e8eef5;
  padding: 8px 14px;
  background: #fafbfc;

  .strip-label {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    color: #9aa0b0;
    margin-bottom: 8px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    cursor: pointer;
    user-select: none;
    transition: color 0.15s;

    &:hover { color: $mainColor; }

    i { font-size: 12px; }

    .strip-count {
      margin-left: 2px;
      background: #fde8e8;
      color: $mainColor;
      border-radius: 10px;
      padding: 0 6px;
      font-size: 10px;
      line-height: 16px;
    }

    .strip-toggle {
      margin-left: auto;
      font-size: 10px;
      transition: transform 0.25s ease;
      color: #b0b8cc;

      &.collapsed {
        transform: rotate(-90deg);
      }
    }
  }

  .strip-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .pill-item {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    border-radius: 20px;
    background: #fff;
    border: 1px solid #e2e8f4;
    cursor: pointer;
    font-size: 12.5px;
    color: #4a5568;
    transition: all 0.18s ease;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    &:hover {
      background: #fde8e8;
      border-color: $mainColor;
      color: $mainColor;
      box-shadow: 0 2px 8px rgba(195, 0, 15, 0.18);
      overflow: visible;
      max-width: 300px;
      z-index: 100;
    }

    &.active {
      background: $mainColor;
      border-color: $mainColor;
      color: #fff;
    }

    .pill-num {
      font-size: 10px;
      font-weight: 700;
      opacity: 0.6;
      flex-shrink: 0;
    }

    .pill-text {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    // hover 弹出完整问题卡片
    .pill-popup {
      display: none;
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      min-width: 240px;
      max-width: 360px;
      background: #fff;
      border: 1px solid #e2e8f4;
      border-radius: 10px;
      padding: 10px 14px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      z-index: 999;
      pointer-events: none;

      &::before {
        content: '';
        position: absolute;
        top: -5px;
        left: 16px;
        width: 9px;
        height: 9px;
        background: #fff;
        border-left: 1px solid #e2e8f4;
        border-top: 1px solid #e2e8f4;
        transform: rotate(45deg);
      }

      .popup-content {
        font-size: 12.5px;
        color: #2d3748;
        line-height: 1.6;
        word-break: break-all;
      }
    }

    &:hover .pill-popup {
      display: block;
    }
  }
}

.conversation-list {
  border-bottom: 1px solid #e8eef5;
  flex-shrink: 0;
  max-height: 200px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: max-height 0.3s ease;
  user-select: none;

  &.collapsed {
    max-height: 38px;
  }

  .list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px 6px;
    font-size: 12px;
    color: #8c92a4;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: $mainColor;
    }

    i {
      margin-right: 5px;
      font-size: 12px;
    }

    .conv-count {
      background: #ebeef5;
      color: #909399;
      border-radius: 10px;
      padding: 1px 8px;
      font-size: 11px;
    }
  }

  .conv-items {
    overflow-y: auto;
    flex: 1;
    padding: 0 8px 8px;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #dcdfe6;
      border-radius: 2px;
    }
  }

  .conv-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 2px;
    position: relative;

    &:hover {
      background: #eef1f7;

      .conv-actions {
        opacity: 1;
      }
    }

    &.active {
      background: linear-gradient(135deg, rgba(195, 0, 15, 0.12), rgba(138, 0, 10, 0.12));

      .conv-icon {
        background: $primary;
        i {
          color: #fff;
        }
      }

      .conv-name {
        color: $primary-solid;
      }
    }

    .conv-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #e8ecf4;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      i {
        font-size: 15px;
        color: #909399;
      }
    }

    .conv-info {
      flex: 1;
      min-width: 0;

      .conv-name {
        font-size: 13px;
        color: #303133;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .conv-time {
        font-size: 11px;
        color: #b4bccf;
        margin-top: 1px;
      }
    }

    .conv-actions {
      opacity: 0;
      transition: opacity 0.2s;
      flex-shrink: 0;

      .del-btn {
        width: 22px;
        height: 22px;
        border: none;
        border-radius: 50%;
        background: rgba(245, 108, 108, 0.12);
        color: #f56c6c;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding: 0;
        transition: all 0.2s;

        i {
          font-size: 10px;
        }

        &:hover {
          background: rgba(245, 108, 108, 0.25);
          transform: scale(1.1);
        }
      }
    }
  }

  .empty-convs {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;
    color: #c0c4cc;

    i {
      font-size: 32px;
      margin-bottom: 8px;
    }

    p {
      margin: 0;
      font-size: 13px;
    }

    .hint {
      font-size: 12px;
      margin-top: 4px;
      color: #d0d6e8;
    }
  }
}

.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: #f5f7fa;

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d0d6e8;
      border-radius: 3px;
    }
  }

  .welcome-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    padding: 20px;

    .welcome-icon {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: $primary;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
      box-shadow: 0 4px 16px rgba(195, 0, 15, 0.35);

      i {
        font-size: 28px;
        color: #fff;
      }
    }

    h2 {
      font-size: 16px;
      color: #303133;
      margin: 0 0 8px;
      font-weight: 600;
    }

    p {
      font-size: 13px;
      color: #909399;
      margin: 0 0 20px;
      max-width: 260px;
      line-height: 1.6;
    }

    .quick-prompts {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: center;

      .prompt-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 14px;
        border: 1px solid #e0e4f0;
        border-radius: 20px;
        background: #fff;
        color: #606266;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.2s;

        i {
          font-size: 13px;
          color: $primary-solid;
        }

        &:hover {
          border-color: $primary-solid;
          color: $primary-solid;
          box-shadow: 0 2px 8px rgba(195, 0, 15, 0.15);
          transform: translateY(-1px);
        }
      }
    }
  }
}

.message-item {
  display: flex;
  margin-bottom: 16px;
  animation: fadeInUp 0.3s ease;
  transition: background 0.3s;

  &.msg-active {
    background: rgba(195, 0, 15, 0.08);
    border-radius: 10px;
    animation: msgHighlight 1.5s ease-out;
  }

  &.user {
    flex-direction: row-reverse;

    .message-content {
      align-items: flex-end;
    }

    .message-bubble {
      .message-text {
        background: $primary;
        color: #fff;
        border-radius: 16px 16px 4px 16px;
      }
    }
  }

  &.assistant {
    .message-bubble {
      .message-text {
        background: #fff;
        color: #303133;
        border-radius: 16px 16px 16px 4px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

        // Markdown 富文本样式
        ::v-deep {
          h1, h2, h3 {
            font-weight: 600;
            margin: 10px 0 6px;
            line-height: 1.4;
            color: #1a1a2e;
          }
          h1 { font-size: 16px; }
          h2 { font-size: 14.5px; }
          h3 { font-size: 13.5px; }

          p {
            margin: 6px 0;
          }

          strong {
            font-weight: 600;
            color: #2c2c3e;
          }

          em {
            font-style: italic;
          }

          del {
            text-decoration: line-through;
            color: #909399;
          }

          ul, ol {
            margin: 6px 0;
            padding-left: 20px;
          }
          ul { list-style-type: disc; }
          ol { list-style-type: decimal; }
          li {
            margin: 3px 0;
            line-height: 1.6;
          }

          blockquote {
            margin: 6px 0;
            padding: 6px 12px;
            border-left: 3px solid $mainColor;
            background: #f5f7ff;
            border-radius: 0 4px 4px 0;
            color: #606266;
          }

          .md-code-block {
            margin: 8px 0;
            padding: 10px 12px;
            background: #1e1e2e;
            border-radius: 8px;
            overflow-x: auto;
            code {
              color: #cdd6f4;
              font-size: 12.5px;
              font-family: 'Fira Code', 'Cascadia Code', 'Consolas', monospace;
              white-space: pre;
            }
          }

          .md-inline-code {
            padding: 1px 5px;
            background: #f0f2f7;
            border-radius: 3px;
            font-size: 12px;
            color: $mainColor;
            font-family: 'Fira Code', 'Cascadia Code', 'Consolas', monospace;
          }
        }
      }
    }
  }

  .message-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin: 0 8px;

    i {
      font-size: 16px;
      color: #fff;
    }
  }

  .user & .message-avatar {
    background: $primary;
  }

  .assistant & .message-avatar {
    background: linear-gradient(135deg, #34d399, #10b981);
  }

  .message-content {
    max-width: 75%;
    display: flex;
    flex-direction: column;

    .message-bubble {
      .message-text {
        padding: 10px 14px;
        line-height: 1.6;
        font-size: 13.5px;
        word-break: break-word;

        &.loading {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 12px 16px;

          .dot {
            width: 7px;
            height: 7px;
            background: #909399;
            border-radius: 50%;
            animation: bounce 1.4s infinite ease-in-out both;

            &:nth-child(1) { animation-delay: -0.32s; }
            &:nth-child(2) { animation-delay: -0.16s; }
          }
        }
      }
    }

    .message-time {
      font-size: 10px;
      color: #b4bccf;
      margin-top: 4px;
      padding: 0 4px;
    }
  }
}

.result-cards {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-card {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e8ecf4;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.22s ease;
  min-height: 68px;

  &:hover {
    border-color: $primary-solid;
    box-shadow: 0 3px 10px rgba(195, 0, 15, 0.14);
    transform: translateY(-1px);
  }

  &.image {
    .card-thumb {
      width: 56px;
      height: 56px;
      border-radius: 6px;
      overflow: hidden;
      flex-shrink: 0;
      margin-right: 12px;
      background: #f0f2f7;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .card-thumb-placeholder {
        color: #c0c4cc;
        font-size: 22px;
      }
    }
  }

  &.article {
    .card-info {
      flex: 1;
    }

    .card-arrow {
      color: #c0c4cc;
      font-size: 14px;
      flex-shrink: 0;
      margin-left: 8px;
    }
  }

  .card-info {
    flex: 1;
    min-width: 0;
  }

  .card-type-tag {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 10px;
    color: #909399;
    margin-bottom: 3px;

    i {
      font-size: 11px;
    }
  }

  .card-title {
    font-size: 13px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .card-summary {
    font-size: 11px;
    color: #606266;
    line-height: 1.4;
    margin-bottom: 3px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .card-meta {
    font-size: 10px;
    color: #b4bccf;
    display: flex;
    flex-wrap: wrap;
    gap: 5px;

    span {
      &::after {
        content: '·';
        margin: 0 5px;
      }

      &:last-child::after {
        content: '';
      }
    }
  }
}

.chat-footer {
  padding: 10px 14px 14px;
  background: #fafbfc;
  border-top: 1px solid #e8ecf4;
  flex-shrink: 0;

  .source-select {
    margin-bottom: 8px;

    ::v-deep .el-radio-button__inner {
      font-size: 12px;
      padding: 4px 10px;
    }
  }

  .input-wrapper {
    display: flex;
    gap: 8px;
    align-items: flex-end;

    ::v-deep .el-textarea {
      .el-textarea__inner {
        border-radius: 10px;
        border-color: #e0e4f0;
        font-size: 13px;
        line-height: 1.5;
        resize: none;

        &:focus {
          border-color: $primary-solid;
          box-shadow: 0 0 0 2px rgba(195, 0, 15, 0.12);
        }
      }
    }

    .send-btn {
      width: 64px;
      height: 60px;
      border: none;
      border-radius: 10px;
      background: #e0e4f0;
      color: #b4bccf;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      cursor: pointer;
      transition: all 0.25s;
      flex-shrink: 0;
      padding: 0;

      i {
        font-size: 18px;
      }

      span {
        font-size: 11px;
      }

      &.active {
        background: $primary;
        color: #fff;
        box-shadow: 0 3px 10px rgba(195, 0, 15, 0.35);
      }

      &:hover.active {
        transform: scale(1.03);
        box-shadow: 0 4px 14px rgba(102, 126, 234, 0.45);
      }
    }
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes msgHighlight {
  0% { background: rgba(195, 0, 15, 0.2); }
  100% { background: transparent; }
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}
</style>
